/**
 * Engineering Timeline derivation — pure, React-free.
 *
 * {@link buildTimeline} folds the entities that already exist (workstreams,
 * documents, decisions, blockers, goal milestones) plus optional explicit
 * {@link TimelineEvent}s into a single chronological stream of
 * {@link DerivedTimelineEvent}s, then filters that stream to the requested
 * {@link TimelineScope} (a goal, a workstream, a service, or the whole team).
 *
 * Every event carries cross-entity links back to its source so the view can
 * navigate; scope filtering is done over those links. Nothing here counts or
 * ranks people — it is a shared memory of what happened and why.
 *
 * NOTE on dates: not every source carries a true "created" timestamp, so we use
 * the most faithful available signal per source (documented inline). Events
 * whose date is unparseable are dropped rather than guessed.
 */

import type {
  DerivedTimelineEvent,
  TimelineEventSummary,
  TimelineLinks,
  TimelineScope,
} from './types';
import type { Goal } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';
import type { BlockerSummary } from '../blockers/types';
import type { Service } from '../services/types';

/** Everything the derivation reads. */
export interface TimelineInputs {
  /** Full goal documents (needed for milestones + titles). */
  goals: Goal[];
  workstreams: WorkstreamSummary[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  blockers: BlockerSummary[];
  /** Optional persisted explicit events. */
  explicitEvents: TimelineEventSummary[];
  /** When the scope is a service, the resolved Service (expands its linked ids). */
  scopeService?: Service;
}

/** Parses a yyyy-mm-dd or ISO date to epoch millis; undefined when unparseable. */
function toTimestamp(date: string | undefined): number | undefined {
  if (!date) return undefined;
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  const t = d.getTime();
  return Number.isNaN(t) ? undefined : t;
}

/** Pushes an event only when its date is parseable. */
function pushEvent(
  out: DerivedTimelineEvent[],
  ev: Omit<DerivedTimelineEvent, 'timestamp'>,
): void {
  const ts = toTimestamp(ev.date);
  if (ts === undefined) return;
  out.push({ ...ev, timestamp: ts });
}

/** Derives the full (unscoped) event stream from all inputs. */
function deriveAll(inputs: TimelineInputs): DerivedTimelineEvent[] {
  const out: DerivedTimelineEvent[] = [];

  // Workstreams. WorkstreamSummary carries no created timestamp, so lastUpdated
  // is the most faithful "this became/was active" signal available.
  for (const w of inputs.workstreams) {
    pushEvent(out, {
      id: `ws:${w.id}`,
      kind: 'workstream_started',
      title: `Workstream: ${w.title || 'Untitled'}`,
      detail: w.currentStage ? `Stage: ${w.currentStage}` : undefined,
      date: w.lastUpdated,
      source: { type: 'workstream', id: w.id, label: w.title },
      links: { workstreamId: w.id, goalId: w.goalId, engineer: w.engineer },
    });
  }

  // Documents: a "created" event, and an "approved" event when approved.
  for (const d of inputs.documents) {
    const links: TimelineLinks = {
      docId: d.id,
      goalId: d.relatedGoalId,
      workstreamId: d.relatedWorkstreamId,
      serviceRefs: d.relatedServices,
      engineer: d.ownerPoc || undefined,
    };
    pushEvent(out, {
      id: `doc-created:${d.id}`,
      kind: 'document_created',
      title: `Document created: ${d.title || 'Untitled'}`,
      detail: [d.docType, d.summary].filter(Boolean).join(' — ') || undefined,
      date: d.createdDate,
      source: { type: 'document', id: d.id, label: d.title },
      links,
    });
    if (d.status === 'APPROVED') {
      pushEvent(out, {
        id: `doc-approved:${d.id}`,
        kind: 'document_approved',
        title: `Document approved: ${d.title || 'Untitled'}`,
        detail: d.docType || undefined,
        date: d.lastReviewedDate || d.createdDate,
        source: { type: 'document', id: d.id, label: d.title },
        links,
      });
    }
  }

  // Decisions.
  for (const dec of inputs.decisions) {
    pushEvent(out, {
      id: `dec:${dec.id}`,
      kind: 'decision_made',
      title: `Decision: ${dec.decision || 'Untitled'}`,
      detail: dec.why || dec.decisionMade || undefined,
      date: dec.date,
      source: { type: 'decision', id: dec.id, label: dec.decision },
      links: {
        decisionId: dec.id,
        goalId: dec.relatedGoalId,
        workstreamId: dec.relatedWorkstreamId,
        serviceRefs: dec.affectedServices,
        docId: dec.relatedDocId,
      },
    });
  }

  // Blockers: raised (blockingSince), and resolved (lastUpdated proxy) when resolved.
  for (const b of inputs.blockers) {
    const links: TimelineLinks = {
      workstreamId: b.blockedWorkstreamId,
      goalId: b.affectedGoalId,
      storyId: b.blockedStoryId,
      engineer: b.blockedEngineer || undefined,
    };
    pushEvent(out, {
      id: `blk-raised:${b.id}`,
      kind: 'blocker_raised',
      title: `Blocker raised: ${b.summary || 'Untitled'}`,
      detail: b.dependencyOwner ? `Dependency owner: ${b.dependencyOwner}` : undefined,
      date: b.blockingSince,
      source: { type: 'blocker', id: b.id, label: b.summary },
      links,
    });
    if (b.status === 'RESOLVED') {
      pushEvent(out, {
        id: `blk-resolved:${b.id}`,
        kind: 'blocker_resolved',
        title: `Blocker resolved: ${b.summary || 'Untitled'}`,
        date: b.lastUpdated,
        source: { type: 'blocker', id: b.id, label: b.summary },
        links,
      });
    }
  }

  // Goal milestones.
  for (const g of inputs.goals) {
    const ms = g.nextMilestone;
    if (!ms?.dueDate) continue;
    pushEvent(out, {
      id: `ms:${g.id}:${ms.label}:${ms.dueDate}`,
      kind: 'milestone',
      title: `Milestone: ${ms.label || 'Milestone'}`,
      detail: g.title,
      date: ms.dueDate,
      source: { type: 'goal', id: g.id, label: g.title },
      links: { goalId: g.id },
    });
  }

  // Explicit events.
  for (const e of inputs.explicitEvents) {
    pushEvent(out, {
      id: `evt:${e.id}`,
      kind: e.kind || 'custom',
      title: e.title || 'Event',
      detail: e.description || undefined,
      date: e.date,
      source: { type: 'event', id: e.id, label: e.title },
      links: {
        goalId: e.relatedGoalId,
        workstreamId: e.relatedWorkstreamId,
        serviceRefs: e.relatedServiceId ? [e.relatedServiceId] : undefined,
        docId: e.relatedDocId,
        decisionId: e.relatedDecisionId,
        storyId: e.relatedStoryId,
        engineer: e.engineer,
      },
    });
  }

  return out;
}

/** Case-insensitive membership test over a list. */
function includesCi(list: string[] | undefined, value: string): boolean {
  const v = value.toLowerCase();
  return (list ?? []).some((x) => x.toLowerCase() === v);
}

/** Decides whether a derived event belongs in the requested scope. */
function matchesScope(
  ev: DerivedTimelineEvent,
  scope: TimelineScope,
  scopeService?: Service,
): boolean {
  if (scope.type === 'all' || !scope.id) return true;
  const id = scope.id;
  if (scope.type === 'goal') return ev.links.goalId === id;
  if (scope.type === 'workstream') return ev.links.workstreamId === id;
  if (scope.type === 'service') {
    // Direct reference by service id or name.
    if (includesCi(ev.links.serviceRefs, id)) return true;
    if (scopeService) {
      if (includesCi(ev.links.serviceRefs, scopeService.name)) return true;
      if (ev.links.docId && (scopeService.documentIds ?? []).includes(ev.links.docId))
        return true;
      if (
        ev.links.decisionId &&
        (scopeService.recentDecisionIds ?? []).includes(ev.links.decisionId)
      )
        return true;
      if (
        ev.links.workstreamId &&
        (scopeService.activeWorkstreamIds ?? []).includes(ev.links.workstreamId)
      )
        return true;
      if (ev.links.goalId && (scopeService.currentGoalIds ?? []).includes(ev.links.goalId))
        return true;
    }
    return false;
  }
  return true;
}

/**
 * Builds the scoped, chronological (newest-first) timeline. De-duplicates by
 * event id (defensive) and sorts by timestamp descending, tie-breaking on a
 * stable id compare.
 */
export function buildTimeline(
  inputs: TimelineInputs,
  scope: TimelineScope = { type: 'all' },
): DerivedTimelineEvent[] {
  const all = deriveAll(inputs);
  const seen = new Set<string>();
  const scoped: DerivedTimelineEvent[] = [];
  for (const ev of all) {
    if (seen.has(ev.id)) continue;
    if (!matchesScope(ev, scope, inputs.scopeService)) continue;
    seen.add(ev.id);
    scoped.push(ev);
  }
  return scoped.sort((a, b) => {
    if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
    return a.id.localeCompare(b.id);
  });
}
